











import { defineComponent, onBeforeMount, ref, computed, ComputedRef } from "@vue/composition-api";
import CollectionFilter from "@/components/ai-results/CollectionFilter.vue";
import { Collection } from "@/types/collection/collection";
import storeHelper from "@/store/store-helper";
import VueI18n from "vue-i18n";

interface CollectionFilterItem {
    collection_id: number;
    name: string | VueI18n.TranslateResult;
    children: CollectionFilterItem[];
    visible: boolean;
}

export default defineComponent({
    name: "CollectionFilterContainer",
    components: { CollectionFilter },
    props: {
        portalId: { type: [Number, null], required: true },
        rootCollectionName: { type: String, default: "" },
        activeCollectionId: { type: Number, default: null },
        visibleCollectionIds: { type: [Array, null], default: () => [] }
    },
    setup(props, { emit }) {
        /**
         * The collection tree of this portal
         */
        const collectionTree = ref<Collection[]>([]);

        /**
         * Load the collectiont tree before mounting the component
         */
        onBeforeMount(async () => {
            collectionTree.value = await storeHelper.portalStore.getCollections(props.portalId);
        });

        /**
         * make a computed ref that returns a collection with the computed tree
         * it changes when props.visibleCollectionIds changes and when the collection tree changes,
         * because they are both dependencies of the computed ref
         */
        const collection: ComputedRef<CollectionFilterItem> = computed(() => {
            return {
                collection_id: null,
                name: props.rootCollectionName,
                children: decendantCollections.value,
                visible: isRootCollectionVisible.value
            };
        });

        const shouldShowAllChildren = computed(_ => props.visibleCollectionIds == null);
        const shouldShowSelectedChildren = computed(_ => props.visibleCollectionIds?.length > 0);

        /**
         * The collection tree of this portal, mapped to a collection filter item including children and visibility
         * @type {CollectionFilterItem[]}
         */
        const decendantCollections: ComputedRef<CollectionFilterItem[]> = computed(() => {
            let decendants: CollectionFilterItem[] = collectionTree.value.map(mapCollectionToFilterItem);

            if (shouldShowAllChildren.value) {
                setAllCollectionsVisible(decendants);
            } else if (shouldShowSelectedChildren.value) {
                setCollectionVisibility(props.visibleCollectionIds as number[], decendants);
            }
            return decendants;
        });

        /**
         * Check if the collection is visible
         */
        const isRootCollectionVisible = computed(() => {
            //first load the collection tree before we can show the collection filter
            if (collectionTree.value == null || collectionTree.value.length == 0) return false;

            if (shouldShowAllChildren.value) return true;
            else if (shouldShowSelectedChildren.value) return true;
            else return false;
        });

        /**
         * Map a collection to a collection filter item
         * @param collection
         */
        const mapCollectionToFilterItem = (collection: Collection): CollectionFilterItem => {
            return {
                collection_id: collection.collection_id,
                name: collection.name,
                children: collection.children?.map(mapCollectionToFilterItem),
                visible: false
            };
        };

        /**
         * Set the visibility of the collections in the collection tree
         * @param searchRelatedCollectionIds - the collection ids that are found in the search results
         * @param collectionFilterTree - the collection tree
         */
        const setCollectionVisibility = (searchRelatedCollectionIds: number[], collectionFilterTree: CollectionFilterItem[]) => {
            collectionFilterTree?.forEach(collection => {
                collection.visible = searchRelatedCollectionIds.includes(collection.collection_id);
                setCollectionVisibility(searchRelatedCollectionIds, collection.children);
            });
        };

        /**
         * Set all collections visible
         * @param collectionFilterTree
         */
        const setAllCollectionsVisible = (collectionFilterTree: CollectionFilterItem[]): void => {
            const allCollectionIds = collectionFilterTree.map(i => i.collection_id);
            setCollectionVisibility(allCollectionIds, collection.value.children);
        };

        /**
         * handle the collection changed click event
         */
        const handleCollectionChanged = (collectionId: number) => {
            emit("collection-clicked", collectionId);
        };

        return {
            props,
            handleCollectionChanged,
            collection
        };
    }
});
